import type { z } from 'zod'
import { viewItemSchema } from '@integration-layer/schemas/GAEvents/viewItem'
import snakeCase from 'lodash/snakeCase'
import type { ListResponse, Price } from '@commercelayer/sdk'
import type { Sku } from '@commercelayer/sdk'
import { productSchema } from '@integration-layer/.nuxt/armani/product-schema'
import { extractCategoriesUnlocalized } from '@design-system/utils/formatAlgoliaHierarchicalCategories'

type ViewItem = z.infer<typeof viewItemSchema>
type AlgoliaProductType = z.infer<typeof productSchema>

/**
 * Generates a Google Analytics 4 view item event for a product.
 *
 * This composable prepares detailed product view tracking information
 * for Google Analytics, including product details, pricing, and availability.
 *
 * @param {AlgoliaProductType} algoliaProduct - The product details from Algolia
 * @param {ListResponse<Price> | null} clPricesAndStocks - Commerce Layer prices and stock information
 * @param {Object} [options] - Additional configuration options
 * @param {boolean} [options.waitForDatalayerSetup=false] - Wait for datalayer to be ready before sending event
 *
 * @returns {void}
 *
 * @example
 * // Track a product view for a specific item
 * useGAViewItemEvent(productDetails, skuPrices, { waitForDatalayerSetup: true })
 *
 * @remarks
 * - Skips event generation if not in client-side context
 * - Handles product availability, pricing, and discount calculations
 * - Supports different brand and category formatting
 */
export const useGAViewItemEvent = (
  algoliaProduct: AlgoliaProductType,
  clPricesAndStocks: ListResponse<Price> | null,
  options?: { waitForDatalayerSetup: boolean }
) => {
  // Client-side check and product validation
  if (!import.meta.client) return
  const isAlgoliaProductValid = Object.keys(algoliaProduct).length > 0
  if (!isAlgoliaProductValid) return

  // Category extraction and currency handling
  const { category1, category2, category4 } = extractCategoriesUnlocalized(
    algoliaProduct?.hierarchicalCategories || [],
    algoliaProduct?.categoryPageId2 || []
  )
  const { getCurrencySymbolFromCountryCode } = useCurrency()
  const { country } = useRouteHelper()

  // Price and discount calculations
  const firstClPrice = clPricesAndStocks?.[0]
  const amountFloat = firstClPrice?.amount_float ?? algoliaProduct.FullPrice
  const discount =
    firstClPrice?.compare_at_amount_float &&
    firstClPrice?.compare_at_amount_float > amountFloat
      ? firstClPrice?.compare_at_amount_float - amountFloat
      : undefined

  const isProductOnCommercelayerDefined =
    clPricesAndStocks && clPricesAndStocks.length > 0

  // Route and availability handling
  const { pdpPath } = useRouteHelper()
  const availableProduct =
    clPricesAndStocks?.find(el => el.sku?.stock_items?.[0]?.quantity ?? 0 > 0)
      ?.sku ?? firstClPrice?.sku
  const availableItem: ViewItem['ecommerce']['items'] = [
    {
      affiliation: 'armani.com',
      coupon: '',
      discount: discount ?? 0,
      index: 1,
      item_availability: availableProduct
        ? isInStock(availableProduct)
          ? 1
          : 0
        : 0,
      item_brand:
        algoliaProduct.Brand.toLowerCase() === 'ea7'
          ? 'EA7'
          : snakeCase(algoliaProduct.Brand ?? 'armani group'),
      item_category: snakeCase(category1),
      item_category2: snakeCase(category2),
      item_category4: snakeCase(category4),
      item_discount: discount ? 1 : 0,
      item_id: algoliaProduct.objectID ?? '',
      item_img: availableProduct?.image_url ?? '',
      item_list_id: 'category',
      item_list_name: snakeCase(`${algoliaProduct.Brand}-${category2}`),
      item_mfc_id: algoliaProduct.MFC ?? '',
      item_name: algoliaProduct.Name ?? '',
      item_reservable: 0,
      item_size: '',
      item_variant:
        algoliaProduct.MacroColorDesc?.split('_')?.[1] ??
        algoliaProduct.MacroColorDesc ??
        '',
      price: availableProduct?.prices?.[0].amount_float ?? 0,
      quantity: isProductOnCommercelayerDefined ? 1 : 0,
      item_url: useRequestURL().origin + pdpPath(algoliaProduct),
    },
  ]

  // Currency and event preparation
  const currency = getCurrencySymbolFromCountryCode(country)
  const event: ViewItem = {
    event: 'view_item',
    eventID: '005',
    ecommerce: {
      currency: currency?.code ?? 'EUR',
      view_type: 'product_page',
      value: firstClPrice?.amount_float ?? 0,
      items: availableItem,
    },
  }

  // Send GA event with optional configurations
  useGASendEvent(viewItemSchema, event, {
    clearEcommerce: true,
    waitForDatalayerSetup: options?.waitForDatalayerSetup,
  })
}
